import { get } from 'lodash';
import {
  equipmentCoverageLayers,
  greatestArea,
  coatingBreakdownCoverageGrade,
  paintSurveyCoverageGrade,
  ClientExtraEquipmentText,
} from '@/utils';

// ToDo: Only used to create CSV files - delete after refactoring CSV logic
//  Format data for display in equipmentTable - original mockUpdatedSearchAsset
export const formatAggregateFields = (inspectionConfig) => (originalData) => {
  const { name, data, _id } = originalData;
  const {
    meta,
    metrics: { corrosion_category, surface_area, coverage },
  } = data;

  const dataFields = {
    name,
    type: data.asset_class,
    images: data.referring_images,
    equipment_id: _id,
    remediated: data.metrics.remediated,
    'primary-category': ['Clean', 'Light', 'Moderate', 'Heavy'][corrosion_category],
    'service-class': meta['AutoCad:Service'],
    size: meta['AutoCad:Size'],
    'equipment-class': meta['AutoCad:Class'],
    'asset-tot': surface_area.total,
    group: meta['AutoCad:Group'],
  };
  return Object.entries(inspectionConfig.corrosionLayers).reduce((fields, [key, { averageKey }]) => {
    // eslint-disable-next-line no-param-reassign
    fields[key] = (coverage[key] ? coverage[key].area / surface_area.total : 0).toFixed(2);
    if (averageKey) {
      // eslint-disable-next-line no-param-reassign
      fields[averageKey] = (coverage[key] ? coverage[key].area : 0).toFixed(2);
    }
    return fields;
  }, dataFields);
};

const getCustomData = (extraEquipmentText, coverageLayers, surfaceArea) => {
  switch (extraEquipmentText) {
    case ClientExtraEquipmentText.COATING_BREAKDOWN:
      return { coatingDamage: coatingBreakdownCoverageGrade(coverageLayers, surfaceArea) };
    case ClientExtraEquipmentText.DEGREE_OF_RUSTING:
      return { degreeOfRusting: paintSurveyCoverageGrade(coverageLayers, surfaceArea) };
    default:
      return {};
  }
};

const getObservedArea = (inspectionConfig, extraEquipmentText) => {
  switch (extraEquipmentText) {
    case ClientExtraEquipmentText.COATING_BREAKDOWN:
      return inspectionConfig.observedArea.coatingBreakdown;
    case ClientExtraEquipmentText.DEGREE_OF_RUSTING:
      return inspectionConfig.observedArea.degreeOfRusting;
    default:
      return [];
  }
};

// ToDo: Only used to create CSV files - delete after refactoring CSV logic
export const formatNonAggregateFields = (inspectionConfig, extraEquipmentText) => (originalData) => {
  const { name, data, _id } = originalData;
  const {
    meta,
    metrics: { corrosion_category, surface_area, coverage },
  } = data;
  const dataFields = {
    name,
    type: data.asset_class,
    images: data.referring_images,
    equipment_id: _id,
    remediated: data.metrics.remediated,
    'primary-category': ['Clean', 'Light', 'Moderate', 'Heavy'][corrosion_category],
    'service-class': meta['AutoCad:Service'],
    size: meta['AutoCad:Size'],
    'equipment-class': meta['AutoCad:Class'],
    'asset-tot': surface_area.total,
    group: meta['AutoCad:Group'] || 'Line',
  };

  const corrosionIncludedFields = Object.entries(inspectionConfig.corrosionLayers).reduce(
    (fields, [key, { averageKey }]) => {
      // eslint-disable-next-line no-param-reassign
      fields[key] = (coverage[key] ? coverage[key].area / surface_area.total : 0).toFixed(2);
      if (averageKey) {
        // eslint-disable-next-line no-param-reassign
        fields[averageKey] = (coverage[key] ? coverage[key].area : 0).toFixed(2);
      }
      return fields;
    },
    dataFields
  );
  const coverageLayers = equipmentCoverageLayers(inspectionConfig.corrosionLayers, originalData);
  const customData = {
    greatestArea: greatestArea(coverageLayers).friendlyName,
    ...getCustomData(extraEquipmentText, coverageLayers, surface_area.total),
  };

  const observedArea = getObservedArea(inspectionConfig, extraEquipmentText);
  return observedArea.reduce((fields, { value }) => {
    // eslint-disable-next-line no-param-reassign
    fields[value] = customData[value];
    return fields;
  }, corrosionIncludedFields);
};

const aggregate = (fields, label) => {
  switch (fields.length) {
    case 0:
      return '';
    case 1:
      return `1 ${label}`;
    default:
      return `${fields.length} ${label}s`;
  }
};

export const applyBinning = (value, bins) =>
  get(
    bins.find(({ min, inclusive }) => value > min || (inclusive && value === min)),
    'display',
    'Bin not found'
  );

export const extractNestedFields = (corrosionLayers, headers, data, customFields = {}) =>
  data.map((fields) => {
    /* eslint-disable no-param-reassign */
    const extractedData = headers.reduce(
      ({ dataFields, coveragePercentageBins }, { value, path, defaultValue, aggregateField, bins }) => {
        switch (value) {
          case 'coveragePercentage':
            coveragePercentageBins = bins;
            break;
          default: {
            if (aggregateField) {
              dataFields[value] = aggregate(get(fields, aggregateField), defaultValue);
            } else {
              dataFields[value] = get(fields, path || value, defaultValue);
            }
            if (bins) {
              dataFields[value] = applyBinning(dataFields[value], bins);
            }
          }
        }

        return { dataFields, coveragePercentageBins };
      },
      { dataFields: {}, coveragePercentageBins: 0 }
    );

    // Add corrosionLayers e.g. AC-L, AC-M and AC-H and average keys and calculate total averageKey coverage
    const { coverage, surface_area: surfaceArea } = fields.data.metrics;
    const combined = corrosionLayers.reduce(
      (layers, [key, { averageKey }]) => {
        layers.fields[key] = coverage[key] ? coverage[key].area / surfaceArea.total : 0;
        if (averageKey) {
          const coverageArea = coverage[key] ? coverage[key].area : 0;

          layers.totalCoverage += coverageArea;
          fields[averageKey] = coverageArea;
        }
        return layers;
      },
      { fields: extractedData.dataFields, totalCoverage: 0 }
    );
    /* eslint-enable no-param-reassign */

    let coveragePercentage = surfaceArea.total > 0 ? (combined.totalCoverage / surfaceArea.total) * 100 : 0;
    if (extractedData.coveragePercentageBins) {
      coveragePercentage = applyBinning(coveragePercentage, extractedData.coveragePercentageBins);
    }

    // Add any extra required custom fields
    return Object.entries(customFields).reduce(
      (custom, [key, value]) => ({
        ...custom,
        [key]: get(fields, value),
      }),
      { ...combined.fields, coveragePercentage }
    );
  });
